'use client';

import { cn } from '@lib/util/cn';
import { LoadingImage } from '@modules/common/components/loading-image';

import type React from 'react';

type ThumbnailOwnProps<T extends React.ElementType = 'a'> = {
  as?: T;
  thumbnail?: string | null;
  // TODO: Fix image typings
  images?: { url: string }[] | null;
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'full' | 'square';
  isFeatured?: boolean;
  className?: string;
  imageClassName?: string;
  'data-testid'?: string;
  preload?: boolean;
  fetchPriority?: 'high' | 'low' | 'auto';
  alt?: string;
  sizes?: string;
};

type ThumbnailProps<T extends React.ElementType = 'a'> = ThumbnailOwnProps<T> &
  Omit<React.ComponentPropsWithoutRef<T>, keyof ThumbnailOwnProps<T>>;

const DEFAULT_THUMBNAIL_SIZES =
  '(max-width: 479px) calc(50vw - 20px), (max-width: 1099px) calc(50vw - 24px), calc(25vw - 40px)';

function Thumbnail<T extends React.ElementType = 'a'>({
  thumbnail,
  images,
  size = 'small',
  isFeatured,
  className,
  imageClassName,
  'data-testid': dataTestid,
  preload,
  fetchPriority,
  as,
  alt,
  sizes,
  ...props
}: ThumbnailProps<T>): React.JSX.Element {
  const Component = as ?? 'a';
  const initialImage = thumbnail || images?.[0]?.url;

  let priorityProps: Record<string, unknown> = {};
  if (fetchPriority === 'high') {
    priorityProps = { fetchPriority, loading: 'eager' as const };
  } else if (fetchPriority) {
    priorityProps = { fetchPriority };
  } else if (preload) {
    priorityProps = { preload: true };
  }

  return (
    <Component
      className={cn(
        'relative w-full overflow-hidden p-4 bg-ui-bg-subtle shadow-elevation-card-rest rounded-large group-hover:shadow-elevation-card-hover transition-shadow ease-in-out duration-150',
        className,
        {
          'aspect-[11/14]': isFeatured,
          'aspect-square':
            (!isFeatured && size !== 'square') || size === 'square',
          'w-[100px]': size === 'xsmall',
          'w-[180px]': size === 'small',
          'w-[290px]': size === 'medium',
          'w-[440px]': size === 'large',
          'w-full': size === 'full',
        },
      )}
      data-testid={dataTestid}
      {...props}
    >
      <LoadingImage
        src={initialImage}
        // Empty-string alt is correct for purely decorative grid thumbnails
        // when no product context is available; meaningful alt should be
        // passed by callers that have product data (PDP gallery, search
        // result cards). Keeping an empty fallback rather than a generic
        // string ("Product thumbnail") avoids polluting screen-reader and
        // image-search signals with duplicate, contentless alt text.
        alt={alt ?? ''}
        className={cn(
          'absolute inset-0 object-contain object-center',
          imageClassName,
        )}
        draggable={false}
        quality={50}
        sizes={sizes ?? DEFAULT_THUMBNAIL_SIZES}
        fill
        wrapperClassName="absolute inset-0"
        placeholderIconSize={size === 'small' ? 16 : 24}
        {...priorityProps}
      />
    </Component>
  );
}

export default Thumbnail;
